import { useLingui } from '@lingui/react/macro';
import { Image } from '@/routes/-components/image';
import { Suspense } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { Expand, ExpandGroup, EXPAND_STEP_ROOMY } from '../expand';

/**
 * The funded-projects figure, live from the same query the homepage counts on. Its own component so the
 * Suspense boundary wraps the number alone — the photo and the headline beside it need no data and
 * should not wait for one.
 */
function ProjectsFunded() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const { language } = useTranslation();
	return <>{stats.projectsFunded.toLocaleString(language, { useGrouping: true })}</>;
}

/**
 * "Una historia con impacto social" — the About hero.
 *
 * Two columns off the hand-off: a 899x645 photo carrying the headline, and a 437-wide stack of the app
 * card over the figure card. Both columns come to the same height, which is why the right one splits
 * 373 + 248 with the same 24px gutter that separates the columns.
 */
export function Hero() {
	const { t } = useLingui();
	return (
		// Left to right: the photograph, then the right column's pair, the figure card trailing the app
		// card above it by half of what a neighbouring column does. `lead`, because this hero hydrates
		// immediately and has no Suspense boundary to wait behind — see `ExpandGroup`.
		<ExpandGroup lead={1} step={EXPAND_STEP_ROOMY} asChild>
			<section className="mx-auto grid max-w-[1440px] gap-6 px-6 pt-6 pb-16 md:pb-24 lg:grid-cols-[899fr_437fr] xl:px-10">
				{/* The scrim the copy is read against. The hand-off draws a flat black at 20%, which was enough
				    for a photograph that happened to be dark where the headline falls — this one is rows of
				    WHITE cloth, and 20% everywhere left the subline washed out on a phone, where the crop
				    puts it straight over the brightest seats. A bottom-anchored gradient instead: the type
				    gets a floor to sit on and the top of the picture keeps the light it was shot with. */}
				<Expand index={0} asChild>
					<div className="relative flex min-h-[420px] items-end overflow-hidden rounded-3xl lg:min-h-[645px]">
						<Image
							src="/redesign/about/hero-auditorium.jpg"
							alt={t`Rows of auditorium seats draped with Worldcoo covers`}
							height={1290}
							aspectRatio={899 / 645}
							objectFit="cover"
							className="absolute inset-0 size-full object-cover!"
						/>
						<div
							aria-hidden
							className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 via-45% to-black/10"
						/>
						<div className="relative flex flex-col gap-4 p-6 md:p-14">
							<h1 className="max-w-[546px] text-4xl leading-[1.143] font-medium tracking-[-0.02em] text-white md:text-[3.5rem]">
								{t`A story with social impact`}
							</h1>
							<p className="max-w-[584px] text-base leading-[1.5] text-white">
								{t`What if we could transform the world every time we use our card?`}
							</p>
						</div>
					</div>
				</Expand>

				<div className="flex flex-col gap-6">
					{/* #FFEDDF is the hand-off's own value for this card and is NOT in the palette page, so it
					    stays a literal rather than borrowing `peach` (#f8ddc9), which is a different colour.

					    `-v2` in the name, not a replacement of the file it supersedes: `public/` goes up
					    `immutable` with nothing to invalidate it, so a new name is the only thing that reaches
					    a browser (TEC-3842, same reason as `solution-*-wide-v2.jpg`). The export's 1024x1536
					    artboard is cropped to the drawing's own bounds, which is what makes `w-[77%]` the
					    size the card was built around. */}
					<Expand index={1} asChild>
						<div className="flex items-center justify-center overflow-hidden rounded-3xl bg-[#FFEDDF] lg:h-[373px]">
							<img
								src="/redesign/about/app-impact-v2.svg"
								alt={t`The Worldcoo app showing a donor's impact`}
								className="h-auto w-[77%]"
							/>
						</div>
					</Expand>

					<Expand index={1.5} asChild>
						<div className="bg-ink flex flex-col justify-between gap-8 rounded-3xl p-8 lg:h-[248px] lg:px-10">
							<Zap className="size-8 shrink-0 fill-current text-white" strokeWidth={0} />
							<div className="flex flex-col">
								<p className="text-olive text-5xl leading-[1.3] font-medium tabular-nums md:text-[4rem]">
									<Suspense fallback={<span className="opacity-40">—</span>}>
										<ProjectsFunded />
									</Suspense>
								</p>
								<p className="text-xl leading-[1.5] font-medium text-white/70 md:text-2xl">{t`Funded projects`}</p>
							</div>
						</div>
					</Expand>
				</div>
			</section>
		</ExpandGroup>
	);
}
